/**
 * MERGE ASSETS — image loader for merge objects (pickups + flask bodies).
 *
 * In-world creature layers (disc, creature, blink, glint) are baked at a scale chosen from the
 * size they are shown at: draw sites report it (`noteLayerSize`: device px of the frame's long
 * side, at rest) and the bake takes the smallest of 1×, 1.5× and 2× that covers it. Small
 * tiers on a phone stay at 1× (a quarter of the 2× memory, on the GPU too). A bake only ever
 * grows, so a bigger window sharpens the art once and never thrashes. Frame paths use
 * `peekLayer`, which never draws: a missing or too coarse layer is queued for idle time and
 * the caller draws what it has (or the composite) meanwhile.
 */
import * as C from "../config/constants.js";
import { getEvolutionRadiusSteps } from "../flask/evolution-sizing.js";
import { mergeTierImageUrl } from "./merge-tier-urls.js";
import { getCachedImage, preloadImageUrl, preloadImageUrls } from "./image-cache.js";
import { runtimeFlags } from "../core/runtime-flags.js";
import { TIER_ART_SIZES, tierLayerKey } from "../render/toon/art-catalog.js";
import {
  bakeToonArtCanvas,
  getToonArtCanvasScale,
  peekToonArtCanvas,
  queueToonArtWarm,
} from "../render/toon/art-registry.js";

var DEFAULT_IMAGE_BODY_RADIUS_RATIO = 0.45;

/** In-world body layers (render/toon/art-catalog.js `tierLayerKey`), bottom to top. */
var TIER_LAYER_NAMES = {
  disc: "tierDisc",
  body: "tierBody",
  blink: "tierBlink",
  shine: "tierShine",
};

/** Bake order when warming a tier: what a body shows first, the blink last. */
var WARM_ORDER = ["disc", "body", "shine", "blink"];

/**
 * The glint is the same picture on every tier (the disc radius is set by the frame's short
 * side, 256 for every creature, and it is centred), so one shine canvas serves all tiers:
 * one texture instead of eleven mostly empty ones.
 */
var SHARED_SHINE_ASSET = "cell";

/** Layer backing-store multiples, smallest first. */
var LAYER_SCALES = [1, 1.5, 2];
/** A bake may be shown up to this much larger than its pixels before it is re-baked finer. */
var LAYER_UPSCALE_SLACK = 1.1;
/** Scale when nobody said how big a layer is shown (tools, tests): the old fixed 2×. */
var DEFAULT_LAYER_SCALE = 2;

/** Tier slice for preload (matches `MAX_MERGE_EVOLUTION_TIERS`). */
function evolutionChainForPreload() {
  var chain = C.EVOLUTION_CHAIN || [];
  var cap = C.MAX_MERGE_EVOLUTION_TIERS;
  var n = chain.length;
  if (cap != null) {
    var parsed = Math.floor(Number(cap));
    if (isFinite(parsed)) {
      n = Math.max(1, Math.min(chain.length, parsed));
    }
  }
  return chain.slice(0, n);
}

function getTierConfig(tier) {
  var chain = C.EVOLUTION_CHAIN;
  if (!chain || !chain[tier]) return null;
  return chain[tier];
}

function getAssetId(tier) {
  var cfg = getTierConfig(tier);
  return cfg ? (cfg.assetId || null) : null;
}

function getTierImage(assetId) {
  if (!assetId) return null;
  return getCachedImage(mergeTierImageUrl(assetId));
}

/**
 * Sprite layout for a merge body (Pixi or Canvas). The `assetCenter` point sits at (cx, cy).
 * @returns {{
 *   image: CanvasImageSource,
 *   x: number,
 *   y: number,
 *   width: number,
 *   height: number,
 *   rotation: number,
 *   anchorX: number,
 *   anchorY: number,
 *   physicsAngle: number,
 *   radiusX: number,
 *   radiusY: number,
 * } | null}
 */
function layoutMergeObject(cx, cy, radius, tier, angle, radiusY) {
  var radiusX = radius;
  if (radiusY == null) radiusY = radiusX;
  var maxRadius = Math.max(radiusX, radiusY);
  var cfg = getTierConfig(tier);
  var ellipseAngle = (cfg && cfg.ellipseAngle != null) ? cfg.ellipseAngle : 0;
  var imageAngle = angle || 0;
  var assetId = getAssetId(tier);
  var img = getTierImage(assetId);
  if (!img || !(img.naturalWidth > 0) || !(img.naturalHeight > 0)) return null;
  var center = (cfg && cfg.assetCenter) || [0.5, 0.5];
  var ratio = (cfg && cfg.imageBodyRadiusRatio != null) ? cfg.imageBodyRadiusRatio : DEFAULT_IMAGE_BODY_RADIUS_RATIO;
  var iw = img.naturalWidth;
  var ih = img.naturalHeight;
  var maxSize = maxRadius / ratio;
  var scale = maxSize / Math.max(iw, ih);
  return {
    image: img,
    x: cx,
    y: cy,
    width: iw * scale,
    height: ih * scale,
    rotation: imageAngle,
    anchorX: center[0],
    anchorY: center[1],
    physicsAngle: imageAngle + ellipseAngle,
    radiusX: radiusX,
    radiusY: radiusY,
  };
}

function layerAssetId(tier, layer) {
  return layer === "shine" ? SHARED_SHINE_ASSET : getAssetId(tier);
}

function layerKeyOf(tier, layer) {
  var name = TIER_LAYER_NAMES[layer];
  if (!name) return null;
  var assetId = layerAssetId(tier, layer);
  if (!assetId || !TIER_ART_SIZES[assetId]) return null;
  return tierLayerKey(name, assetId);
}

/**
 * Logical frame (w, h) of a tier's art: the size every bake of it is a multiple of. Layers
 * are sized from these (never from a canvas's pixels), so a layer may be baked finer or
 * coarser than the composite and still stack exactly; the shared glint has the cell's frame.
 * @param {number} tier
 * @param {"disc"|"body"|"blink"|"shine"} [layer] omitted: the composite `tier/<id>` frame
 * @returns {{ w: number, h: number }|null}
 */
function getTierFrame(tier, layer) {
  var assetId = layer ? layerAssetId(tier, layer) : getAssetId(tier);
  var size = assetId ? TIER_ART_SIZES[assetId] : null;
  return size ? { w: size[0], h: size[1] } : null;
}

/** Device px of each tier's frame (long side) as drawn, the largest seen (per tier). */
var layerNeedPx = Object.create(null);
/** Jar bodies keep fixed proportions between tiers (jarRadiusRatios): device px per unit of that ratio. */
var jarNeedPerRatio = 0;
/** Fall pickups are one size for every tier. */
var fallNeedPx = 0;
/** The shared glint is drawn on every tier: the largest need of any tier. */
var shineNeedPx = 0;

var jarRadiusRatios = null;

/** Per-tier jar radius proportions, the same ones flask/body-sizing.js scales to a jar. */
function radiusRatio(tier) {
  if (!jarRadiusRatios) {
    jarRadiusRatios = getEvolutionRadiusSteps(C.getActiveEvolutionChain(), C.FLASK_EVOLUTION_SIZING)
      || C.FLASK_BODY_RADIUS_RATIOS
      || [];
  }
  var r = jarRadiusRatios[tier];
  return r > 0 ? r : 0;
}

function tierNeedPx(tier) {
  var need = Math.max(layerNeedPx[tier] || 0, fallNeedPx);
  var r = radiusRatio(tier);
  if (jarNeedPerRatio > 0 && r > 0) need = Math.max(need, jarNeedPerRatio * r);
  return need;
}

/** Smallest layer scale whose bake covers `px` (device px of the frame's long side). */
function layerScaleForPx(px, assetId) {
  if (!(px > 0)) return DEFAULT_LAYER_SCALE;
  var size = TIER_ART_SIZES[assetId];
  var ratio = px / (size ? Math.max(size[0], size[1]) : 256);
  for (var i = 0; i < LAYER_SCALES.length; i++) {
    if (LAYER_SCALES[i] * LAYER_UPSCALE_SLACK >= ratio) return LAYER_SCALES[i];
  }
  return LAYER_SCALES[LAYER_SCALES.length - 1];
}

function wantedLayerScale(tier, layer) {
  if (layer === "shine") return layerScaleForPx(shineNeedPx, SHARED_SHINE_ASSET);
  return layerScaleForPx(tierNeedPx(tier), getAssetId(tier));
}

/**
 * Report how big a tier's art is drawn: device px of its frame's long side at rest (no pop or
 * squash), from the Fall ("fall": pickups share one size) or the jar ("jar": radii keep fixed
 * tier proportions, so the tiers a merge will make are sized before they exist).
 * Cheap enough to call per body per frame.
 * @param {number} tier
 * @param {number} devicePx
 * @param {"fall"|"jar"} [source]
 */
function noteLayerSize(tier, devicePx, source) {
  if (!(devicePx > 0) || !isFinite(devicePx)) return;
  if (devicePx > (layerNeedPx[tier] || 0)) layerNeedPx[tier] = devicePx;
  if (devicePx > shineNeedPx) shineNeedPx = devicePx;
  if (source === "fall") {
    if (devicePx > fallNeedPx) fallNeedPx = devicePx;
  } else if (source === "jar") {
    var r = radiusRatio(tier);
    if (r > 0 && devicePx / r > jarNeedPerRatio) jarNeedPerRatio = devicePx / r;
  }
}

/** Device px per CSS px of a 2D canvas draw (the game caps the backing store at 2×). */
function canvasDeviceScale() {
  if (typeof window === "undefined" || !(window.devicePixelRatio > 0)) return 1;
  var cap = C.CANVAS_MAX_DPR != null ? Number(C.CANVAS_MAX_DPR) : 2;
  if (!(cap > 0)) cap = 2;
  return Math.max(1, Math.min(cap, window.devicePixelRatio));
}

function queueLayerBake(tier, layer, key) {
  queueToonArtWarm(key, function () {
    var before = peekToonArtCanvas(key);
    var cv = bakeToonArtCanvas(key, wantedLayerScale(tier, layer));
    // Hand only a new canvas to the GPU pre-upload.
    return cv !== before ? cv : null;
  });
}

/**
 * One layer of a tier's in-world art (same frame as the tier picture, so it stacks with the
 * `layoutMergeObject` size and anchor), drawn now if needed. Null without a 2D canvas.
 * Frame paths use `peekTierLayer` instead.
 * @param {number} tier
 * @param {"disc"|"body"|"blink"|"shine"} layer
 * @returns {HTMLCanvasElement|null}
 */
function getTierLayer(tier, layer) {
  var key = layerKeyOf(tier, layer);
  return key ? bakeToonArtCanvas(key, wantedLayerScale(tier, layer)) : null;
}

/**
 * The layer if it is already drawn, else null; never draws. A missing layer, or one drawn
 * coarser than it is now shown, is queued for idle time.
 * @param {number} tier
 * @param {"disc"|"body"|"blink"|"shine"} layer
 * @returns {HTMLCanvasElement|null}
 */
function peekTierLayer(tier, layer) {
  var key = layerKeyOf(tier, layer);
  if (!key) return null;
  var cv = peekToonArtCanvas(key);
  if (!cv || getToonArtCanvasScale(key) < wantedLayerScale(tier, layer)) queueLayerBake(tier, layer, key);
  return cv;
}

/**
 * Bake a tier's layers in idle time before they are needed (the creatures a Fall will drop,
 * the blink a body will do, the creature a merge will make), so no frame pays for drawing a
 * creature.
 * @param {number} tier
 * @param {number} [devicePx] how big it will be shown (see `noteLayerSize`)
 * @param {"fall"|"jar"} [source]
 */
function prewarmTierLayers(tier, devicePx, source) {
  if (tier == null || tier < 0 || !getAssetId(tier)) return;
  if (devicePx > 0) noteLayerSize(tier, devicePx, source);
  for (var i = 0; i < WARM_ORDER.length; i++) peekTierLayer(tier, WARM_ORDER[i]);
}

/**
 * Pre-encode a tier's picture URL (`tier/<id>`, the DOM chips and the canvas aspect source)
 * in idle time, so the first body of a new tier does not encode it inside a frame.
 * @param {number} tier
 */
function prewarmTierUrl(tier) {
  var assetId = getAssetId(tier);
  if (!assetId || !TIER_ART_SIZES[assetId]) return;
  queueToonArtWarm("url:tier/" + assetId, function () {
    mergeTierImageUrl(assetId);
    return null;
  });
}

/** @internal Test helper: forget reported layer sizes. */
function resetLayerSizesForTests() {
  layerNeedPx = Object.create(null);
  jarNeedPerRatio = 0;
  fallNeedPx = 0;
  shineNeedPx = 0;
}

function drawMergeObject(ctx, cx, cy, radius, tier, fallbackColor, angle, radiusY) {
  var radiusX = radius;
  if (radiusY == null) radiusY = radiusX;
  var maxRadius = Math.max(radiusX, radiusY);
  var isEllipse = Math.abs(radiusX - radiusY) > 0.01;
  var cfg = getTierConfig(tier);
  var ellipseAngle = (cfg && cfg.ellipseAngle != null) ? cfg.ellipseAngle : 0;
  var imageAngle = angle || 0;
  var physicsAngle = imageAngle + ellipseAngle;
  var assetId = getAssetId(tier);
  var img = getTierImage(assetId);

  if (img) {
    var cfg2 = getTierConfig(tier);
    var center = (cfg2 && cfg2.assetCenter) || [0.5, 0.5];
    var cxRatio = center[0];
    var cyRatio = center[1];
    var ratio = (cfg2 && cfg2.imageBodyRadiusRatio != null) ? cfg2.imageBodyRadiusRatio : DEFAULT_IMAGE_BODY_RADIUS_RATIO;

    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    var maxSize = maxRadius / ratio;
    var scale = maxSize / Math.max(iw, ih);
    var drawW = iw * scale;
    var drawH = ih * scale;

    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.translate(cx, cy);
    if (imageAngle) ctx.rotate(imageAngle);
    ctx.translate(-cxRatio * drawW, -cyRatio * drawH);
    ctx.drawImage(img, 0, 0, iw, ih, 0, 0, drawW, drawH);
    ctx.restore();
    if (runtimeFlags.showPhysicsBodyContour) {
      ctx.strokeStyle = runtimeFlags.testFlaskMode ? "#00aaff" : "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (isEllipse) {
        ctx.ellipse(cx, cy, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
      } else {
        ctx.arc(cx, cy, radiusX, 0, Math.PI * 2);
      }
      ctx.stroke();
    }
    return true;
  } else {
    ctx.fillStyle = fallbackColor || "#94a3b8";
    ctx.beginPath();
    if (isEllipse) {
      ctx.ellipse(cx, cy, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
    } else {
      ctx.arc(cx, cy, radiusX, 0, Math.PI * 2);
    }
    ctx.fill();
    if (runtimeFlags.showPhysicsBodyContour) {
      ctx.strokeStyle = runtimeFlags.testFlaskMode ? "#00aaff" : "rgba(255,255,255,0.6)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      if (isEllipse) {
        ctx.ellipse(cx, cy, radiusX, radiusY, physicsAngle, 0, Math.PI * 2);
      } else {
        ctx.arc(cx, cy, radiusX, 0, Math.PI * 2);
      }
      ctx.stroke();
    }
    return false;
  }
}

function preload(tiers) {
  var chain = C.EVOLUTION_CHAIN;
  if (!chain) return Promise.resolve();
  var active = evolutionChainForPreload();
  var toLoad = tiers || active.map(function (_, i) { return i; });
  var urls = [];
  var seen = {};
  for (var i = 0; i < toLoad.length; i++) {
    var aid = getAssetId(toLoad[i]);
    if (!aid || seen[aid]) continue;
    seen[aid] = true;
    var url = mergeTierImageUrl(aid);
    if (url) urls.push(url);
  }
  return preloadImageUrls(urls);
}

/** Load evolution images for active tiers only (see `MAX_MERGE_EVOLUTION_TIERS`). */
function preloadAll() {
  var chain = evolutionChainForPreload();
  if (!chain || chain.length === 0) return Promise.resolve([]);
  var seen = {};
  var urls = [];
  for (var i = 0; i < chain.length; i++) {
    var assetId = chain[i] && chain[i].assetId;
    if (!assetId || seen[assetId]) continue;
    seen[assetId] = true;
    var url = mergeTierImageUrl(assetId);
    if (url) urls.push(url);
  }
  return preloadImageUrls(urls);
}

function ensureLoaded(tier, callback) {
  var assetId = getAssetId(tier);
  if (!assetId) {
    if (callback) callback(null);
    return Promise.resolve(null);
  }
  var cached = getTierImage(assetId);
  if (cached) {
    if (callback) callback(cached);
    return Promise.resolve(cached);
  }
  return preloadImageUrl(mergeTierImageUrl(assetId)).then(function (img) {
    if (callback) callback(img);
    return img;
  });
}

export const MergeAssets = {
  draw: drawMergeObject,
  layout: layoutMergeObject,
  layer: getTierLayer,
  peekLayer: peekTierLayer,
  frame: getTierFrame,
  noteLayerSize: noteLayerSize,
  layerScale: wantedLayerScale,
  canvasDeviceScale: canvasDeviceScale,
  prewarmLayers: prewarmTierLayers,
  prewarmUrl: prewarmTierUrl,
  __resetLayerSizesForTests: resetLayerSizesForTests,
  preload: preload,
  preloadAll: preloadAll,
  ensureLoaded: ensureLoaded,
  DEFAULT_IMAGE_BODY_RADIUS_RATIO: DEFAULT_IMAGE_BODY_RADIUS_RATIO,
};